import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Box, InputAdornment, TextField, Typography } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import { DragDropContext, Draggable, DropResult } from 'react-beautiful-dnd';
import { useLocation, useNavigate } from 'react-router-dom';
import { useProgramContext } from '../../../../../../hooks/useProgramContext';
import useProfileDetails from '../../../../../../hooks/useProfileDetails';
import PillarColumn from './PillarColumn';
import ProgramCard from './ProgramCard';
import AddNewProgramDrawer from './AddNewProgramDrawer';
import LearningFrameworkBuilder from '../learning-framework-builder/LearningFrameworkBuilder';
import { useLearningFrameworkStore } from '../learning-framework-builder/useLearningFrameworkStore';
import AddNewColumnTile from './AddNewColumnTile';
import { toast } from 'react-toastify';
import type { LearningPillar, ProgramCardData, ProgramTypeKey } from './types';
import ProgramTypeFilter from './ProgramTypeFilter';
import { useAdminLearningLifecycle } from './useAdminLearningLifecycle';
import { adminProgramsDue } from './programDueDate';
import SetupGuideModal from './SetupGuideModal';
import FrameworkElliottMenu from './FrameworkElliottMenu';
import { useAdminSetupProgress } from '../../../../../../components/gamification/useAdminSetupProgress';
import AdminGamificationBar from '../../../../../../components/gamification/AdminGamificationBar';
import { API } from '../../../../../../api';
import { useAppDispatch, useAppSelector } from '../../../../../../redux/hooks';
import { navigateToChat } from '../../../NavigateToChat';
import { useOrgAudienceOptions } from './useOrgMemberAttributes';
import { getLLAuthHeaders } from '../../../../utils/llAuthHeaders';
import {
  applyAudienceMatchTypes,
  audienceRulesFromFlat,
  flattenAudienceRules,
  type AudienceMatchType
} from '../../../../utils/memberAttributes';
import useProgramCreationFlowV2 from '../../../../../../hooks/useProgramCreationFlowV2';
import useLearningGaps from '../../../../../../hooks/useLearningGaps';
import { GAPS_PANEL_WIDTH } from './GapsRisksPanel';
import AdminInsightsPanel, { type InsightsView } from './AdminInsightsPanel';
import AdminInsightsPanelLegacy from './AdminInsightsPanelLegacy';
import { useCapabilityReleaseState } from '../../../../../../hooks/useCapabilityRelease';
import RecommendedCourseCard from './RecommendedCourseCard';
import DosenButton from '../../../../../../components/ui/Buttons/DosenButton';
import BuildTrainingDialog from './BuildTrainingDialog';
import ProgramEditorModal from './program-editor/ProgramEditorModal';
import RecommendationReasonDialog from './RecommendationReasonDialog';
import { apiMessage, capabilityApi, type CapabilityRecommendationView } from '../../../../../../api/capability';
import {
  useCapabilityAssessmentDrafts,
  useCapabilityAssessments,
  useCapabilityOrg,
  useCapabilityRecommendations,
  useRefreshCapability
} from '../../../../../../queries/useCapability';
import AssessmentReviewDialog from '../../../capability-setup/AssessmentReviewDialog';
import { draftFromView, type AssessmentDraft } from '../../../capability-setup/create/assessmentDrafts';
import { adminAssessmentCards, draftAssessmentCards, forTeams, type AdminAssessmentCard } from './adminAssessmentCards';
import ResumeAssessmentDialog from './ResumeAssessmentDialog';
import { trainingFlowReady } from '../../../capability-setup/flags';
import {
  currentEpisode,
  isGapClosed,
  type GapEpisode,
  type RecommendationKey,
  type RecommendedCourse
} from './knowledgeGaps';
import { useAdminGamification } from '../../../../../../components/gamification/useAdminGamification';
import { buildGapsClosedSection, type MedalKey } from '../../../../../../components/gamification/scorecardData';
import { useLearningGapsData } from './useLearningGapsData';
import {
  FrameworkColumnsSkeleton,
  InsightsPanelSkeleton,
  fadeInSx
} from '../../../../../../components/ui/DashboardSkeletons';
import { peekOpenProgramIntent, takeOpenProgramIntent } from '../../../../../../utils/openProgramIntent';
import ROLES from '../../../../../../utils/roles';
import { MOBILE_SX, useIsMobileApp } from '../../../../../../mobile/mobileApp';
import PhoneSegments from '../../../../../../mobile/PhoneSegments';

/**
 * Admin Learning Lifecycle Dashboard — pillar-based replacement for the
 * status Kanban (AdminProgramBoard). GA: shown to every org, no flag.
 *
 * The page title "LEARNING FRAMEWORK" (with sparkle icon) lives in the
 * platform chrome via Layout.tsx — not rendered here to avoid duplication.
 *
 * Build progress — see Tech Plan §4:
 *   ✓ Step 1  Page shell + mount
 *   ✓ Step 2  Top bar
 *     Step 3  Pillar columns scaffold
 *     Step 4  Program card
 *     Step 5  Dummy data wire-up
 *     Step 6  Search behaviour
 */

// Nav arrow — Figma spec: bg #565656 (Fill/Button/button-options-default),
// padding 12/10, radius 6, 1px #444444 border, backdrop-blur 1.1px;
// chevron is 1.5px white stroke (Stroke/icon-highlight).
const NavArrowButton: React.FC<{
  direction: 'left' | 'right';
  onClick: () => void;
  'aria-label': string;
}> = ({ direction, onClick, 'aria-label': ariaLabel }) => {
  const Icon = direction === 'right' ? ChevronRightIcon : ChevronLeftIcon;
  return (
    <Box
      component="button"
      onClick={onClick}
      aria-label={ariaLabel}
      sx={{
        position: 'absolute',
        top: 30,
        [direction]: 0,
        pl: '12px',
        pr: '12px',
        pt: '10px',
        pb: '10px',
        bgcolor: '#565656',
        border: '1px solid #444444',
        borderRadius: '6px',
        backdropFilter: 'blur(1.1px)',
        WebkitBackdropFilter: 'blur(1.1px)',
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        cursor: 'pointer',
        transition: 'background-color 0.15s',
        '&:hover': { bgcolor: '#666666' }
      }}
    >
      <Icon sx={{ color: '#FDFDFD', fontSize: 16, strokeWidth: 1.5 }} />
    </Box>
  );
};

// Sentinel id used by the synthetic "Unassigned" column. Programs whose
// learningPillarId is null land here so the admin can drag them into a
// real pillar (PATCH /programs/{id} with the picked id) — or back out by
// dropping into the Unassigned column (PATCH with null).
const UNASSIGNED_DROPPABLE_ID = '__unassigned__';
const UNASSIGNED_PILLAR: LearningPillar = {
  id: UNASSIGNED_DROPPABLE_ID,
  name: 'Unassigned',
  order: -1,
  icon: null
};

// Drag-to-reorder sort. Array.prototype.sort is stable, so cards that share a
// displayOrder (legacy programs all default to 0 until first reordered) keep
// their incoming BE order.
const byDisplayOrder = (a: ProgramCardData, b: ProgramCardData) => (a.displayOrder ?? 0) - (b.displayOrder ?? 0);

// Collapsed program card height. Recommended Course cards match it so the two
// sit level in a column.
export const CARD_HEIGHT = 176;

// Recommendations render as one row across the top of the framework — one per
// pillar, three in total.
const MAX_RECOMMENDATIONS = 3;

// A recommended course on the framework, and every gap round it stands for.
interface FrameworkRecommendation {
  course: RecommendedCourse;
  targets: Array<{ gapId: string; episodeId: string }>;
  /** With the Capability release: the role and capability the Training is recommended for, and why. */
  capability?: CapabilityRecommendationView;
}

const AdminLearningLifecycleDashboard: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  // Existing 1:1 chats — Message opens the thread if there is one.
  const personalThreadsArr = useAppSelector((s) => s.acsChannels.personalThreadsArr);
  const { orgId, coId, groupId, isPlatformAdmin } = useProgramContext();
  // The Capability release's left panel (Capability / Activity) and board title,
  // or the ones before it (utils/capabilityRelease.ts). The panel waits for the answer to be
  // final, so the pre-release Learning Gaps panel never flashes up first.
  const { on: capabilityRelease, resolved: capabilityReleaseResolved } = useCapabilityReleaseState();
  // Publish Training is the build flow here (where it is ready: see capability-setup/flags.ts).
  const trainingFlow = capabilityRelease && trainingFlowReady();
  const InsightsPanel = capabilityRelease ? AdminInsightsPanel : AdminInsightsPanelLegacy;
  const [searchTerm, setSearchTerm] = useState('');
  // Program-type filter. Empty = every type shown (the default); a chip toggles
  // its type in and out. Narrows the framework columns alongside search + teams.
  const [selectedTypes, setSelectedTypes] = useState<ProgramTypeKey[]>([]);
  const toggleType = (key: ProgramTypeKey) =>
    setSelectedTypes((prev) => (prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]));
  const matchesType = (p: ProgramCardData) => selectedTypes.length === 0 || selectedTypes.includes(p.programType);
  const columnsRef = useRef<HTMLDivElement | null>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);
  const [addDrawerOpen, setAddDrawerOpen] = useState(false);
  // Learning Framework Builder (Elliott menu → recommendation flow). Every
  // org; org admins only (the framework is an org-level decision).
  const [frameworkBuilderOpen, setFrameworkBuilderOpen] = useState(false);
  // Creation-flow revamp §1 Case A — Continue on an early-stage AI build
  // reopens the Add Program modal in resume mode for that group.
  // mode 'members' — Continue on a program waiting at the Invite stage.
  const [resumeDrawerGroup, setResumeDrawerGroup] = useState<{
    id: string;
    name: string;
    mode?: 'wizard' | 'members';
  } | null>(null);
  // 'menu' when opened from the Elliott orb (Elliott offers actions); 'program'
  // when opened from a pillar's Add New Program CTA (starts the flow directly).
  const [addDrawerEntry, setAddDrawerEntry] = useState<'program' | 'menu'>('program');
  // Org name (menu copy) + Org Admin gate (Edit Framework action).
  const orgName = useAppSelector((s) => (s.orgDetails.data as { name?: string } | undefined)?.name) || '';
  const { firstName: userName } = useProfileDetails();
  // Team managers reach this dashboard with the plain Member role, so "not a
  // group admin" is no longer "org admin": they get the group-admin variant
  // (no framework editing, no Setup Guide for the org).
  const viewerRole = localStorage.getItem('role') || '';
  const isOrgAdmin = viewerRole !== 'groupAdmin' && viewerRole !== 'Member';
  const frameworkEnabled = isOrgAdmin;
  // Invite Org Admin (Elliott orb) — true org/platform admins only. isOrgAdmin
  // above also lets through program admins whose JWT role is Member.
  const canInviteOrgAdmin = [ROLES.orgAdmin, ROLES.platAdmin].includes(localStorage.getItem('role') || '');
  // The org's saved framework (BE, with a local fallback) — decides the Elliott
  // menu's "Build" vs "See" label and feeds the builder.
  const frameworkStore = useLearningFrameworkStore({
    enabled: frameworkEnabled,
    orgId,
    isPlatformAdmin,
    orgName
  });
  // All existing group names in the org (not just Learning Framework programs) so
  // Elliott can validate a new program name at entry against the same uniqueness
  // set the BE enforces on create — avoiding a late "Group Name already exists".
  const allOrgGroupNames = useAppSelector(
    (s) => (s.allGroups.data?.groups || []).map((g) => g.name).filter(Boolean) as string[]
  );
  // Pillar id from which the drawer was opened — determines where a
  // newly-created program lands on Design Later.
  const [addDrawerPillarId, setAddDrawerPillarId] = useState<string | null>(null);
  // Id of the program card whose "Continue" is mid-navigation. While set,
  // that card shows a spinner and EVERY card's Continue is disabled so the
  // admin can't fire several navigations at once.
  const [continuingId, setContinuingId] = useState<string | null>(null);

  // Live data from BE. Reads: GET /orgs/{orgId}/pillars + GET /v3/admin/programs.
  // Mutations exposed by the hook are wired into the handlers below.
  const {
    pillars,
    programs,
    orgMembers,
    loading: frameworkLoading,
    error,
    refetch,
    createPillar,
    updatePillar,
    deletePillar,
    moveProgram,
    reorderPrograms,
    updateProgramLaunchDate,
    updateProgramAudience,
    previewProgramAudience,
    updateProgramOwner,
    updateProgramType,
    createProgramFromDrawer,
    publishMentorshipProgram,
    inviteAdmin,
    promoteOrgAdmin
  } = useAdminLearningLifecycle();
  // First load only: placeholder columns until the board has data. Later
  // refetches (after an edit) keep the real board on screen.
  const frameworkFirstLoad = frameworkLoading && !error && pillars.length === 0 && programs.length === 0;

  // Org attribute lists, used to bucket the Add-Program chat's flat audience
  // selection into per-list rules at the create boundary. The conversational
  // flow still collects a single flat list; bucketing it into one "any" rule
  // per owning list preserves exactly the semantics it has always had.
  const { catalog: audienceCatalog } = useOrgAudienceOptions();
  // Creation-flow revamp: audiences picked while creating are stored only —
  // enrolment waits for Invite & Finish on the Members step.
  const creationFlowV2 = useProgramCreationFlowV2();
  // The chat's flat picks → per-list rules carrying the admin's Any/All choices.
  const chatAudienceRules = (audience: string[], audienceMatch?: Record<string, AudienceMatchType>) =>
    applyAudienceMatchTypes(audienceRulesFromFlat(audience, audienceCatalog), audienceMatch);

  useEffect(() => {
    if (error) toast.error(error);
  }, [error]);

  // ---- Learning Gaps (flag) -----------------------------------------------
  // The whole Learning Gaps half — panel, Recommended Course cards, Teams
  // filter, Gaps Closed score — GA for every org (useLearningGaps is constant
  // flag (or the FE pilot list). Off, the page is the Learning Framework
  // alone, as before.
  const learningGaps = useLearningGaps();

  // ---- Teams + gaps (BE) --------------------------------------------------
  // GET /v3/admin/learning-gaps: the org's Teams list and every team with its
  // gaps, already scoped by the BE (org admins: the whole org; group admins:
  // lesson gaps of the programs they administer). The Teams filter drives both
  // the panel and which programs the framework shows.
  const gapsData = useLearningGapsData(learningGaps, isPlatformAdmin ? orgId : undefined);
  const teamOptions = useMemo(() => gapsData.teams.map((t) => ({ id: t.id, name: t.name })), [gapsData.teams]);
  // Multi-select, defaulting to every team available to this admin — an org
  // admin sees the whole org, a program admin the teams across their programs.
  // At least one must always be selected.
  const [selectedTeamIds, setSelectedTeamIds] = useState<string[]>([]);
  useEffect(() => {
    // No Learning Gaps → no Teams filter: the framework shows every program.
    if (!learningGaps) {
      if (selectedTeamIds.length > 0) setSelectedTeamIds([]);
      return;
    }
    // Seed with all teams once the catalog lands, and drop any selection whose
    // attribute value disappeared org-side (falling back to all).
    if (teamOptions.length === 0) {
      if (selectedTeamIds.length > 0) setSelectedTeamIds([]);
      return;
    }
    const valid = selectedTeamIds.filter((id) => teamOptions.some((t) => t.id === id));
    if (valid.length === 0) {
      setSelectedTeamIds(teamOptions.map((t) => t.id));
    } else if (valid.length !== selectedTeamIds.length) {
      setSelectedTeamIds(valid);
    }
  }, [learningGaps, teamOptions, selectedTeamIds]);

  // The selected teams' gaps — what the panel renders.
  const teamGaps = useMemo(
    () => (learningGaps ? gapsData.teams.filter((t) => selectedTeamIds.includes(t.id)) : []),
    [learningGaps, gapsData.teams, selectedTeamIds]
  );

  // Gaps across EVERY team this admin administers — what the Learning Gaps
  // Closed score is measured over. The Teams filter changes what's displayed,
  // never what's scored: a score that moved with the filter would be a view.
  const allTeamGaps = useMemo(() => (learningGaps ? gapsData.teams : []), [learningGaps, gapsData.teams]);

  // Programs the selected Teams are an audience for. Only programs that
  // actually target the Teams list are narrowed: one with no audience, or one
  // targeting other lists only (Seniority, Region…), says nothing about teams
  // and stays visible rather than vanishing from the framework.
  const teamPrograms = useMemo(() => {
    if (selectedTeamIds.length === 0) return programs;
    const teamValueIds = new Set(teamOptions.map((t) => t.id));
    return programs.filter((p) => {
      const ids = (p.audienceRules ? flattenAudienceRules(p.audienceRules) : p.audience ?? []).map(String);
      const targetedTeams = ids.filter((id) => teamValueIds.has(id));
      return targetedTeams.length === 0 || targetedTeams.some((id) => selectedTeamIds.includes(id));
    });
  }, [programs, selectedTeamIds, teamOptions]);

  // With the Capability release, what Elliott recommends comes from capability scores, not
  // Learning Gaps: a Training for a role whose score on a capability is under 70% with at least
  // half the role assessed (the backend decides; see GET /v3/admin/capability/recommendations).
  const capabilityOrg = useCapabilityOrg();
  const refreshCapability = useRefreshCapability();
  const recommendationTeams =
    selectedTeamIds.length > 0 && selectedTeamIds.length < teamOptions.length ? selectedTeamIds : null;
  const { data: capabilityRecommendations } = useCapabilityRecommendations(recommendationTeams, capabilityRelease);

  // Every Assessment the viewer may see, each a card in its stage's column beside the Trainings
  // (Capability release): those still being built (at Design), then those that have been run.
  // The backend says which those are, who may carry on building one, and how many people an
  // Assessment is for.
  const { data: runAssessments } = useCapabilityAssessments(capabilityRelease);
  const { data: assessmentDrafts } = useCapabilityAssessmentDrafts(capabilityRelease);
  const assessmentCards = useMemo(() => {
    const pillarIds = pillars.map((p) => p.id);
    const drafts = (assessmentDrafts ?? []).map(draftFromView).filter((d): d is AssessmentDraft => d !== null);
    return forTeams(
      [...draftAssessmentCards(drafts, pillarIds), ...adminAssessmentCards(runAssessments ?? [], pillarIds)],
      selectedTeamIds
    );
  }, [assessmentDrafts, runAssessments, pillars, selectedTeamIds]);
  // Continue on an Assessment's card: what it asks, read-only, once it has been run; Run
  // Assessment, where it was left, while it is still being built (for those who may change it).
  const [openAssessment, setOpenAssessment] = useState<AdminAssessmentCard | null>(null);
  const [resumeAssessment, setResumeAssessment] = useState<AssessmentDraft | null>(null);

  // Recommendations the admin has just ignored: off the board at once, without waiting for the
  // list to be read again, and back only if the save fails. Each is "roleId|capabilityId".
  const [ignoredRecommendations, setIgnoredRecommendations] = useState<Set<string>>(() => new Set());

  // Elliott's recommended courses, bucketed by the pillar they belong in: the
  // pillar of the program the originating lesson sits in; a gap built only
  // from learner questions (and every capability recommendation) has no
  // program, so it takes the next free pillar.
  const recommendationsByPillar = useMemo(() => {
    // One card per course name — several selected teams surface the same gaps
    // — standing for every gap round that recommends it, so acting on the card
    // acts on all of them. The framework shows the CURRENT episode's course
    // (earlier ones are history in the panel), and only while it's still open:
    // done or ignored, it's no longer a recommendation.
    const byName = new Map<string, FrameworkRecommendation & { programId: string | null }>();
    // The Capability release: one card per role and capability under the bar, lowest score first.
    for (const rec of capabilityRelease ? capabilityRecommendations ?? [] : []) {
      if (byName.has(rec.course.name) || ignoredRecommendations.has(`${rec.roleId}|${rec.capabilityId}`)) continue;
      byName.set(rec.course.name, {
        course: {
          name: rec.course.name,
          // For that role only. A role is not one of the org's attribute values, so no value ids,
          // and its name is never matched to one.
          audiences: [rec.roleName],
          audienceValueIds: [],
          audienceIsRoles: true,
          purpose: rec.course.purpose,
          tone: [],
          documents: []
        },
        targets: [],
        capability: rec,
        programId: null
      });
    }
    // Before the release: the courses Learning Gaps recommends.
    for (const gap of capabilityRelease ? [] : teamGaps.flatMap((t) => t.gaps)) {
      const episode = currentEpisode(gap);
      const course = episode?.course;
      if (!episode || !course || episode.status.course) continue;
      const entry = byName.get(course.name);
      if (entry) entry.targets.push({ gapId: gap.id, episodeId: episode.id });
      else
        byName.set(course.name, {
          course,
          targets: [{ gapId: gap.id, episodeId: episode.id }],
          programId: gap.programId
        });
    }

    const byPillar = new Map<string, FrameworkRecommendation[]>();
    // Unmatched recommendations round-robin across the pillars rather than
    // piling into the first one, so they read as spread across the framework.
    let nextFallback = 0;
    for (const { programId, ...rec } of byName.values()) {
      // At most one recommendation per pillar, and three across the framework:
      // they sit as a single row above the program cards, not a stack.
      if (byPillar.size >= MAX_RECOMMENDATIONS) break;
      let pillarId: string | null = programId
        ? programs.find((p) => p.id === programId)?.learningPillarId || null
        : null;
      // Already carrying a recommendation, or unresolved — take the next free
      // pillar so the three spread across the row.
      if ((!pillarId || byPillar.has(pillarId)) && pillars.length > 0) {
        const free = pillars.slice(nextFallback).find((p) => !byPillar.has(p.id));
        if (!free) continue;
        pillarId = free.id;
        nextFallback = pillars.findIndex((p) => p.id === free.id) + 1;
      }
      if (!pillarId || byPillar.has(pillarId)) continue;
      byPillar.set(pillarId, [rec]);
    }
    return byPillar;
  }, [teamGaps, programs, pillars, capabilityRelease, capabilityRecommendations, ignoredRecommendations]);

  // Ignore from the card — the same tick as Ignore on Publish Course in the
  // Learning Gaps panel, on every round the card stands for.
  // setStatus reports its own failures (and rolls the tick back).
  const handleIgnoreRecommendedCourse = (rec: FrameworkRecommendation) => {
    if (rec.capability) {
      // A capability recommendation: dismissed until the role recovers to the bar and falls under it again.
      const key = `${rec.capability.roleId}|${rec.capability.capabilityId}`;
      setIgnoredRecommendations((prev) => new Set(prev).add(key));
      capabilityApi
        .setRecommendationStatus(rec.capability.roleId, rec.capability.capabilityId, 'ignored', capabilityOrg)
        .then(() => refreshCapability('admin'))
        .catch((err) => {
          setIgnoredRecommendations((prev) => {
            const next = new Set(prev);
            next.delete(key);
            return next;
          });
          toast.error(apiMessage(err, "Couldn't ignore the recommendation. Please try again."));
        });
      return;
    }
    rec.targets.forEach(({ gapId, episodeId }) => gapsData.setStatus(gapId, episodeId, 'course', 'ignored'));
  };

  // "Publish Course" — hand the recommendation to the Add New Program flow,
  // which opens prefilled so the admin reviews rather than retypes.
  const [recommendedSeed, setRecommendedSeed] = useState<RecommendedCourse | null>(null);
  const handlePublishRecommendedCourse = (course: RecommendedCourse) => {
    // Drop it in the pillar the recommendation was bucketed into, if any.
    let pillarId: string | null = null;
    for (const [id, recs] of recommendationsByPillar.entries()) {
      if (recs.some((r) => r.course.name === course.name)) {
        pillarId = id;
        break;
      }
    }
    // Published from the Learning Gaps panel, a course with no card on the framework (past the first
    // three, or under the Capability release, whose cards are its capability recommendations): the
    // pillar of the program its gap's lesson sits in.
    if (!pillarId) {
      const gap = teamGaps.flatMap((t) => t.gaps).find((g) => currentEpisode(g)?.course?.name === course.name);
      pillarId = (gap?.programId && programs.find((p) => p.id === gap.programId)?.learningPillarId) || null;
    }
    // A capability recommendation first says why Elliott recommends it; the build flow opens from there.
    const capability = Array.from(recommendationsByPillar.values())
      .flat()
      .find((r) => r.course.name === course.name)?.capability;
    if (capability) {
      setRecommendationReason({ course, pillarId, capability });
      return;
    }
    openRecommendedBuild(course, pillarId);
  };
  // Why Elliott recommends a Training, shown before its build flow.
  const [recommendationReason, setRecommendationReason] = useState<{
    course: RecommendedCourse;
    pillarId: string | null;
    capability: CapabilityRecommendationView;
  } | null>(null);
  // The capability recommendation whose Training is being built in Add New Program: marked published
  // once it is under way. Publish Training keeps its recommendation with the program instead.
  const [buildingRecommendation, setBuildingRecommendation] = useState<CapabilityRecommendationView | null>(null);
  const openRecommendedBuild = (
    course: RecommendedCourse,
    pillarId: string | null,
    capability?: CapabilityRecommendationView
  ) => {
    // With the Capability release, the recommendation opens in Publish Training, filled in for
    // review (where that flow is ready: see capability-setup/flags.ts), and is built there.
    // Otherwise it goes to Add New Program as before.
    if (trainingFlow) {
      setTrainingBuild({ recommended: { course, pillarId, capability } });
      return;
    }
    setBuildingRecommendation(capability ?? null);
    setRecommendedSeed(course);
    setAddDrawerPillarId(pillarId);
    setAddDrawerEntry('program');
    setAddDrawerOpen(true);
  };
  // Publish Training, open on the dashboard: from a recommendation (filled in from it), or on a
  // program built in it, reopened where it was left (Continue on its card).
  const [trainingBuild, setTrainingBuild] = useState<{
    recommended?: { course: RecommendedCourse; pillarId: string | null; capability?: CapabilityRecommendationView };
    resume?: { groupId: string; published: boolean };
    // From Add New Program, once the admin chose a Course or Guided Course built with Elliott.
    fresh?: { pillarId: string | null; guided: boolean };
  } | null>(null);
  // The program the open flow has created, so its card can be shown when the flow is left.
  const trainingProgramId = useRef<string | null>(null);
  // The program open in the Program Editor modal (Continue on a live card with lessons). Read from
  // `programs`, so an edit made on its Set up tab and its card on the board are the same program.
  const [editorProgramId, setEditorProgramId] = useState<string | null>(null);
  const editorProgram = editorProgramId ? programs.find((p) => p.id === editorProgramId) ?? null : null;

  // Whether the org already has a knowledge base connected. Checked up front so
  // the Elliott menu can drop the Sync Knowledge action entirely when one
  // exists. 404 / error → treat as not connected (the action stays offered).
  const [hasKnowledgeIntegration, setHasKnowledgeIntegration] = useState(false);
  useEffect(() => {
    if (!orgId) return;
    let cancelled = false;
    void (async () => {
      try {
        const status = await API.getSharePointConnection(orgId);
        if (!cancelled) setHasKnowledgeIntegration(Boolean(status?.isConnected));
      } catch {
        if (!cancelled) setHasKnowledgeIntegration(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [orgId]);

  // Edit Framework state — `draftPillars` holds in-flight edits so the
  // admin can cancel without committing. Save on exit (Save Changes click)
  // dispatches POST /orgs/{orgId}/pillars for newly-added columns and
  // PATCH /pillars/{id} for changed ones.
  // The Setup Guide is no longer offered by the Elliott menu — it opens on the
  // first visit of the day and from the gamification bar's Resume Setup.
  const [setupGuideOpen, setSetupGuideOpen] = useState(false);
  // Setup progress — shown as a badge on Elliott's Setup Guide menu option.
  const setupProgress = useAdminSetupProgress(isOrgAdmin ? 'orgAdmin' : 'groupAdmin');
  // Newly-created program to scroll to + pulse (brandy outline) after Create.
  const [highlightProgramId, setHighlightProgramId] = useState<string | null>(null);
  // The phone stacks the page: the panel, then the board's stages as tabs that open their cards.
  const isMobileApp = useIsMobileApp();
  const [openStages, setOpenStages] = useState<Set<string>>(() => new Set());
  // Gap closure, reported up by the Gaps & Risks panel (which owns the
  // done/ignored state), and scored in the gamification hub.

  // ---- Left panel: Learning Gaps ⇄ Learning Tier ---------------------------
  // Which view the left panel shows — always Gaps & Risks on arrival. The Teams
  // filter above (selectedTeamIds) is shared, so switching keeps the selection.
  const [insightsView, changeInsightsView] = useState<InsightsView>('gaps');
  // Phone: Programs | Capability | Activity, one at a time, as on the learner's dashboard.
  const [phoneView, setPhoneView] = useState<'programs' | InsightsView>('programs');
  // Tier for the selected teams. All teams selected = the admin's whole remit,
  // so no filter is sent. Fetched only while the Tier view is showing.
  const allTeamsSelected = teamOptions.length === 0 || selectedTeamIds.length === teamOptions.length;
  const { medals: teamTierMedals, loading: teamTierLoading } = useAdminGamification(
    allTeamsSelected ? undefined : selectedTeamIds,
    { skip: !learningGaps || insightsView !== 'tier' }
  );
  // Learning Gaps Closed over the SELECTED teams, so it follows the filter too.
  const selectedGapCounts = useMemo(() => {
    const statusByEpisode = new Map<string, GapEpisode['status']>();
    for (const team of teamGaps) {
      for (const gap of team.gaps) {
        for (const episode of gap.episodes) statusByEpisode.set(episode.id, episode.status || {});
      }
    }
    const statusFor = (episodeId: string, key: RecommendationKey) => statusByEpisode.get(episodeId)?.[key];
    const gaps = teamGaps.flatMap((t) => t.gaps);
    return { closed: gaps.filter((g) => isGapClosed(g, statusFor)).length, total: gaps.length };
  }, [teamGaps]);
  const tierMedals = useMemo(() => {
    if (!teamTierMedals) return null;
    const section = buildGapsClosedSection(selectedGapCounts.closed, selectedGapCounts.total);
    const order: MedalKey[] = ['wood', 'bronze', 'silver', 'gold', 'platinum'];
    const overall =
      order.indexOf(section.medal) < order.indexOf(teamTierMedals.overall) ? section.medal : teamTierMedals.overall;
    return { ...teamTierMedals, overall, sections: [section, ...teamTierMedals.sections] };
  }, [teamTierMedals, selectedGapCounts]);

  // The standalone wizard's Invite & Finish navigates here with the finished
  // program's id in navigation state — same scroll+highlight as the embedded
  // finish. Consume it once and clear the history entry's state.
  const routerLocation = useLocation();
  useEffect(() => {
    const fromNav = (routerLocation.state as { highlightProgramId?: string } | null)?.highlightProgramId;
    if (fromNav) {
      setHighlightProgramId(fromNav);
      window.history.replaceState({}, document.title);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [routerLocation.state]);

  // Add Program chat, "Invite New Member" as Program Owner: the name + email
  // used to be collected and dropped. Invite them as a program admin with
  // asOwner, so they become the owner (on signup, if they're new). Best-effort:
  // the program itself is already created.
  const inviteNewOwner = useCallback(
    async (
      grpId: string | null | undefined,
      values: { ownerMode: string | null; inviteFirstName: string; inviteLastName: string; inviteEmail: string }
    ) => {
      if (!grpId || values.ownerMode !== 'invite' || !values.inviteEmail?.trim()) return;
      try {
        await API.inviteProgramCollaborator(grpId, {
          firstName: values.inviteFirstName.trim(),
          lastName: values.inviteLastName.trim(),
          email: values.inviteEmail.trim().toLowerCase(),
          asOwner: true
        });
        refetch();
      } catch (err: any) {
        toast.error(
          err?.response?.data?.message ||
            "The program was created, but the owner invitation couldn't be sent. You can set the owner from the program card."
        );
      }
    },
    [refetch]
  );

  // Co-design invite (bell / email / Teams deep link): once the programs have
  // loaded, highlight the invited program and — while it's still being built —
  // run its Continue so the admin lands in the builder. Consumed once.
  const [autoContinueId, setAutoContinueId] = useState<string | null>(null);
  useEffect(() => {
    if (!programs.length || !peekOpenProgramIntent()) return;
    const id = takeOpenProgramIntent();
    const target = programs.find((p) => p.id === id);
    if (!target) return;
    setHighlightProgramId(target.id);
    if (target.status !== 'invited' && target.status !== 'active') setAutoContinueId(target.id);
  }, [programs]);

  // Scroll the highlighted card into view, and clear the highlight after 5s or
  // on the next click anywhere on the page.
  useEffect(() => {
    if (!highlightProgramId) return;
    const clearTimer = window.setTimeout(() => setHighlightProgramId(null), 10000);
    const onClick = () => setHighlightProgramId(null);
    // Attach after a beat so the click that triggered Create doesn't clear it.
    const attachTimer = window.setTimeout(() => window.addEventListener('click', onClick), 400);
    return () => {
      window.clearTimeout(clearTimer);
      window.clearTimeout(attachTimer);
      window.removeEventListener('click', onClick);
    };
  }, [highlightProgramId]);

  // Auto-launch the setup guide once, on the admin's first visit. After that
  // it never opens itself again: the guide stays one click away via Elliott
  // and the Setup chip, so an admin who has already seen it (or finished setup)
  // is never interrupted. We deliberately do NOT re-open daily.
  useEffect(() => {
    if (!orgId) return;
    const role = localStorage.getItem('role') || '';
    const isGrpAdmin = role === 'groupAdmin';
    const isOrgAdmin = role === 'OrgAdmin';
    if (!isGrpAdmin && !isOrgAdmin) return;

    const completeKey = isGrpAdmin ? `dosen_setup_complete_grp_${orgId}` : `dosen_setup_complete_${orgId}`;
    if (localStorage.getItem(completeKey) === 'true') return;

    const seenKey = isGrpAdmin ? `dosen_setup_seen_grp_${orgId}` : `dosen_setup_seen_${orgId}`;
    if (localStorage.getItem(seenKey) === 'true') return;

    localStorage.setItem(seenKey, 'true');
    setSetupGuideOpen(true);
  }, [orgId]);

  const [editMode, setEditMode] = useState(false);
  const [draftPillars, setDraftPillars] = useState<LearningPillar[]>(pillars);
  // Flips to true after a Save Changes attempt that has partially-filled
  // drafts (icon-only or name-only). Drives the red error styling on
  // those columns. Resets when edit mode is entered or a save succeeds.
  const [editSubmitAttempted, setEditSubmitAttempted] = useState(false);

  // Show the left/right chevrons whenever pillar columns overflow horizontally
  // and there's still room to scroll in that direction. Listener attached
  // once; the layout-change effect below remeasures when the column count
  // shifts (entering edit mode adds the Add new column tile, saving may
  // commit new columns, etc.).
  useEffect(() => {
    const el = columnsRef.current;
    if (!el) return;
    const update = () => {
      setCanScrollLeft(el.scrollLeft > 0);
      setCanScrollRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 1);
    };
    el.addEventListener('scroll', update);
    window.addEventListener('resize', update);
    return () => {
      el.removeEventListener('scroll', update);
      window.removeEventListener('resize', update);
    };
  }, []);

  // Re-measure scroll state when the visible column count changes — toggling
  // edit mode adds/removes the Add new column tile, saving may commit new
  // columns. Without this the arrows would lag a render behind.
  useEffect(() => {
    const el = columnsRef.current;
    if (!el) return;
    // requestAnimationFrame so we read after the new layout is painted.
    const id = requestAnimationFrame(() => {
      setCanScrollLeft(el.scrollLeft > 0);
      setCanScrollRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 1);
    });
    return () => cancelAnimationFrame(id);
  }, [editMode, pillars.length, draftPillars.length]);

  const handleEditLifecycle = async () => {
    if (editMode) {
      // Step 1: silently drop drafts with neither name nor icon (admin
      // started adding them and never followed through).
      const remaining = draftPillars.filter((p) => p.name.trim() !== '' || p.icon !== null);
      // Step 2: check for partial drafts (exactly one of name/icon missing).
      // These trigger the red error styling and block the save.
      const hasPartial = remaining.some((p) => p.name.trim() === '' || p.icon === null);
      if (hasPartial) {
        setDraftPillars(remaining);
        setEditSubmitAttempted(true);
        return;
      }
      // Persist via BE. New drafts (id starts with 'pillar-new-') → POST.
      // Existing drafts that previously existed but were dropped → DELETE.
      // Existing drafts with changed name/icon/order → PATCH.
      const draftIds = new Set(remaining.map((p) => p.id));
      const removedIds = pillars.map((p) => p.id).filter((id) => !draftIds.has(id));
      await Promise.all(removedIds.map((id) => deletePillar(id)));

      const existingById = new Map(pillars.map((p) => [p.id, p]));
      for (const draft of remaining) {
        const isNew = draft.id.startsWith('pillar-new-');
        if (isNew && draft.icon) {
          await createPillar({ name: draft.name.trim(), icon: draft.icon, order: draft.order });
        } else if (!isNew) {
          const prior = existingById.get(draft.id);
          const changes: Partial<Pick<LearningPillar, 'name' | 'icon' | 'order'>> = {};
          if (prior?.name !== draft.name) changes.name = draft.name.trim();
          if (prior?.icon !== draft.icon) changes.icon = draft.icon;
          if (prior?.order !== draft.order) changes.order = draft.order;
          if (Object.keys(changes).length > 0) {
            await updatePillar(draft.id, changes);
          }
        }
      }
      // Refetch to reconcile state with whatever the BE persisted (handles
      // partial failures + the cascade-to-null side-effect of deletes).
      await refetch();
      setEditMode(false);
      setEditSubmitAttempted(false);
    } else {
      setDraftPillars(pillars);
      setEditSubmitAttempted(false);
      setEditMode(true);
    }
  };

  const handlePillarRemove = (id: string) => {
    // Drop from the draft only — the actual DELETE happens on Save Changes
    // (handleEditLifecycle diffs the draft against the server list and
    // dispatches deletePillar for the missing ones).
    setDraftPillars((prev) => prev.filter((p) => p.id !== id));
  };

  // Inline edits committed from a ProgramCard popover. All three fields
  // now persist via dedicated BE endpoints.
  // A card's program type. Once a program's structure is generated it can't move into or out of
  // Role Excellence (that type is built on its roles' capabilities): the backend refuses it (409),
  // and updateProgramType says why and puts the card back.
  const changeProgramType = (program: ProgramCardData, programType: ProgramCardData['programType']) =>
    updateProgramType(program.id, programType);

  const handleProgramFieldChange = (programId: string, changes: Partial<ProgramCardData>) => {
    if (Object.prototype.hasOwnProperty.call(changes, 'launchDate')) {
      updateProgramLaunchDate(programId, changes.launchDate ?? null);
    }
    if (Object.prototype.hasOwnProperty.call(changes, 'audienceRules')) {
      updateProgramAudience(programId, changes.audienceRules ?? null);
    }
    if (Object.prototype.hasOwnProperty.call(changes, 'owner')) {
      updateProgramOwner(programId, changes.owner?.id ?? null);
    }
  };

  // DnD — drag a program card within a column to reorder it, or to a
  // different column to reassign its learningPillarId. Both persist via
  // PATCH /programs/{id} (learningPillarId + displayOrder). The synthetic
  // Unassigned column uses the UNASSIGNED_DROPPABLE_ID sentinel; drops
  // INTO it clear learningPillarId, drops FROM it set the new pillar.
  const handleDragEnd = (result: DropResult) => {
    const { source, destination, draggableId } = result;
    // destination is null when the card is dropped outside any Droppable.
    if (!destination) {
      console.warn('[DnD] drop had no destination — card snapped back', { draggableId, source });
      return;
    }
    const sameColumn = source.droppableId === destination.droppableId;
    // True no-op: dropped back exactly where it started.
    if (sameColumn && source.index === destination.index) return;

    const destPillarId = destination.droppableId === UNASSIGNED_DROPPABLE_ID ? null : destination.droppableId;

    // With an active search the rendered cards are a filtered subset, so the
    // dnd indices don't line up with the full column — renumbering would
    // corrupt the hidden cards' order. Fall back to the plain cross-column
    // move (no precise positioning) and skip same-column reorder.
    const query = searchTerm.trim().toLowerCase();
    if (query) {
      if (!sameColumn) moveProgram(draggableId, destPillarId);
      return;
    }

    const moved = programs.find((p) => p.id === draggableId);
    if (!moved) return;

    // Rebuild the destination column in its current rendered order, drop the
    // moved card in at its new index, then renumber the whole column 0..n.
    const inColumn = (p: ProgramCardData) =>
      destPillarId == null ? !p.learningPillarId : p.learningPillarId === destPillarId;
    const reordered = programs.filter((p) => inColumn(p) && p.id !== draggableId).sort(byDisplayOrder);
    reordered.splice(Math.min(destination.index, reordered.length), 0, moved);

    // Persist only the cards whose pillar or position actually changed, and
    // only those the viewer may edit: a team manager's column can hold
    // read-only programs, whose PATCH would 403 and roll the whole move back.
    // byDisplayOrder tolerates the gaps this leaves in the numbering.
    const updates = reordered
      .map((p, idx) => ({ id: p.id, learningPillarId: destPillarId, displayOrder: idx }))
      .filter((u) => {
        const cur = programs.find((p) => p.id === u.id);
        if (!cur || !cur.canEdit) return false;
        return cur.displayOrder !== u.displayOrder || (cur.learningPillarId || '') !== (u.learningPillarId || '');
      });
    if (updates.length > 0) reorderPrograms(updates);
  };

  const handleAddColumn = () => {
    setDraftPillars((prev) => [
      ...prev,
      {
        id: `pillar-new-${Date.now()}`,
        name: '',
        order: prev.length,
        icon: null // unset — renders the placeholder circle until admin picks one
      }
    ]);
  };

  const handlePillarChange = (id: string, changes: Partial<LearningPillar>) => {
    setDraftPillars((prev) => prev.map((p) => (p.id === id ? { ...p, ...changes } : p)));
  };

  // Shared card renderer — used by both the Unassigned column and the
  // pillar .map below so the Draggable + ProgramCard scaffolding stays
  // in one place.
  const renderProgramDraggable = (program: ProgramCardData, index: number) => (
    <Draggable
      key={program.id}
      draggableId={program.id}
      index={index}
      // No drag during edit-lifecycle mode — keep card moves
      // separate from structural pillar edits. None on the phone either: a
      // long press while scrolling could move a program, and reordering
      // stays a desktop action.
      isDragDisabled={editMode || !program.canEdit || isMobileApp}
    >
      {(dragProvided, snapshot) => {
        const isHighlighted = highlightProgramId === program.id;
        return (
          <Box
            ref={(el: HTMLElement | null) => {
              dragProvided.innerRef(el);
              // Scroll the freshly-created (highlighted) card into view.
              if (el && isHighlighted) el.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'center' });
            }}
            {...dragProvided.draggableProps}
            {...dragProvided.dragHandleProps}
            sx={{
              // minHeight (not a fixed height) so an expanding card grows smoothly
              // in step with its Collapse animation, instead of snapping to 'auto'
              // in a single frame — which jerked the dial/pipeline. Collapsed, the
              // content sits at CARD_HEIGHT, matching the Recommended Course cards
              // to the pixel so the two line up across columns.
              flex: '0 0 auto',
              minHeight: CARD_HEIGHT,
              // The phone's card is one compact row: no tile height to fill.
              [MOBILE_SX]: { minHeight: 0 },
              display: 'flex',
              flexDirection: 'column',
              boxShadow: snapshot.isDragging ? '0 12px 32px rgba(0, 0, 0, 0.4)' : 'none',
              opacity: snapshot.isDragging ? 0.95 : 1,
              transition: 'box-shadow 0.15s'
            }}
          >
            <ProgramCard
              program={program}
              readOnly={!program.canEdit}
              highlighted={isHighlighted}
              autoContinue={autoContinueId === program.id}
              existingMembers={orgMembers}
              onAudienceChange={(audienceRules) => handleProgramFieldChange(program.id, { audienceRules })}
              onAudiencePreview={(rules) => previewProgramAudience(program.id, rules)}
              onLaunchDateChange={(launchDate) => handleProgramFieldChange(program.id, { launchDate })}
              onOwnerChange={(owner) => handleProgramFieldChange(program.id, { owner })}
              onProgramTypeChange={(programType) => changeProgramType(program, programType)}
              continuingId={continuingId}
              onContinueStart={setContinuingId}
              onResumeInDrawer={(groupId, programName) => {
                // Resuming in the modal doesn't navigate away, so release the
                // shared Continue lock the click just took.
                setContinuingId(null);
                setResumeDrawerGroup({ id: groupId, name: programName });
                setAddDrawerEntry('program');
                setAddDrawerOpen(true);
              }}
              onOpenMembers={(groupId, programName) => {
                setContinuingId(null);
                setResumeDrawerGroup({ id: groupId, name: programName, mode: 'members' });
                setAddDrawerEntry('program');
                setAddDrawerOpen(true);
              }}
              onOpenEditor={(p) => {
                // The modal opens over the board without navigating, so release the Continue lock.
                setContinuingId(null);
                setEditorProgramId(p.id);
              }}
              onResumeTraining={
                trainingFlow
                  ? (groupId, published) => {
                      setContinuingId(null);
                      setTrainingBuild({ resume: { groupId, published } });
                    }
                  : undefined
              }
            />
          </Box>
        );
      }}
    </Draggable>
  );

  const handleScrollLeft = () => {
    const el = columnsRef.current;
    if (!el) return;
    el.scrollBy({ left: -el.clientWidth * 0.6, behavior: 'smooth' });
  };

  const handleScrollRight = () => {
    const el = columnsRef.current;
    if (!el) return;
    el.scrollBy({ left: el.clientWidth * 0.6, behavior: 'smooth' });
  };

  // The board's columns, in order. The desktop lays them side by side; the phone stacks them as
  // tabs, all shut to begin with. Editing the framework keeps every column open.
  const boardQuery = searchTerm.trim().toLowerCase();
  const boardColumns: Array<{ pillar: LearningPillar; hasCards: boolean; node: React.ReactNode }> = [];
  const stageTab = (id: string) =>
    isMobileApp && !editMode
      ? {
          collapsed: !openStages.has(id),
          onToggleCollapsed: () =>
            setOpenStages((prev) => {
              const next = new Set(prev);
              if (!next.delete(id)) next.add(id);
              return next;
            })
        }
      : {};
  // Unassigned column — synthetic, shown only when there are programs without a learningPillarId
  // AND we're not in edit mode. Lets the admin drag legacy programs into a real pillar; vanishes
  // once empty.
  if (!editMode && !frameworkFirstLoad) {
    const unassigned = teamPrograms
      .filter(
        (p) =>
          (!p.learningPillarId || p.learningPillarId === '') &&
          (boardQuery === '' || p.name.toLowerCase().includes(boardQuery)) &&
          matchesType(p)
      )
      .sort(byDisplayOrder);
    if (unassigned.length > 0) {
      boardColumns.push({
        pillar: UNASSIGNED_PILLAR,
        hasCards: true,
        node: (
          <PillarColumn
            key={UNASSIGNED_DROPPABLE_ID}
            pillar={UNASSIGNED_PILLAR}
            programCount={unassigned.length}
            {...stageTab(UNASSIGNED_PILLAR.id)}
          >
            {unassigned.map((program, index) => renderProgramDraggable(program, index))}
          </PillarColumn>
        )
      });
    }
  }
  for (const pillar of editMode ? draftPillars : pillars) {
    // PRD §6: case-insensitive partial-match against program name only.
    // Filter runs across all columns simultaneously; columns with zero
    // matches stay visible but show an empty body + 0 count.
    const visiblePrograms = teamPrograms
      .filter(
        (p) =>
          p.learningPillarId === pillar.id &&
          (boardQuery === '' || p.name.toLowerCase().includes(boardQuery)) &&
          matchesType(p)
      )
      .sort(byDisplayOrder);
    // Recommended courses are matched by the same name search as the
    // program cards, and hidden entirely in edit mode.
    const pillarRecommendations = editMode
      ? []
      : (recommendationsByPillar.get(pillar.id) ?? []).filter(
          (r) => boardQuery === '' || r.course.name.toLowerCase().includes(boardQuery)
        );
    // Assessments answer to the same name search and type filter as the program cards.
    const pillarAssessments = assessmentCards.filter(
      (a) =>
        a.card.learningPillarId === pillar.id &&
        (boardQuery === '' || a.card.name.toLowerCase().includes(boardQuery)) &&
        matchesType(a.card)
    );
    const count = visiblePrograms.length + pillarAssessments.length;
    boardColumns.push({
      pillar,
      hasCards: count > 0 || pillarRecommendations.length > 0,
      node: (
        <PillarColumn
          key={pillar.id}
          pillar={pillar}
          programCount={count}
          {...stageTab(pillar.id)}
          assessments={
            pillarAssessments.length > 0
              ? pillarAssessments.map((a) => (
                  <Box
                    key={a.card.id}
                    data-testid="assessment-card"
                    data-assessment-id={a.card.id}
                    data-stage={a.info.status}
                    data-completion={a.card.programCompletionRate}
                    sx={{
                      flex: '0 0 auto',
                      minHeight: CARD_HEIGHT,
                      [MOBILE_SX]: { minHeight: 0 },
                      display: 'flex',
                      flexDirection: 'column'
                    }}
                  >
                    <ProgramCard
                      program={a.card}
                      readOnly
                      assessment={{
                        ...a.info,
                        onOpen: a.draft
                          ? a.draft.canEdit
                            ? () => setResumeAssessment(a.draft)
                            : undefined
                          : () => setOpenAssessment(a)
                      }}
                      continuingId={null}
                    />
                  </Box>
                ))
              : undefined
          }
          recommendations={
            pillarRecommendations.length > 0
              ? pillarRecommendations.map((rec) => (
                  <RecommendedCourseCard
                    key={rec.course.name}
                    course={rec.course}
                    height={CARD_HEIGHT}
                    onPublish={handlePublishRecommendedCourse}
                    onIgnore={() => handleIgnoreRecommendedCourse(rec)}
                  />
                ))
              : undefined
          }
          editMode={editMode}
          onPillarChange={(changes) => handlePillarChange(pillar.id, changes)}
          onRemove={() => handlePillarRemove(pillar.id)}
          showErrors={editSubmitAttempted}
        >
          {visiblePrograms.map((program, index) => renderProgramDraggable(program, index))}
        </PillarColumn>
      )
    });
  }

  // Phone: a search opens the tabs with results, so they show without a tap, and clearing it shuts
  // them again. They can still be shut by hand while searching.
  const searchedStages = boardQuery
    ? boardColumns
        .filter((c) => c.hasCards)
        .map((c) => c.pillar.id)
        .join('|')
    : '';
  useEffect(() => {
    if (isMobileApp) setOpenStages(new Set(searchedStages ? searchedStages.split('|') : []));
  }, [isMobileApp, searchedStages]);

  // Phone: a highlighted card has to be on screen to scroll to, so its tab opens.
  useEffect(() => {
    const target = isMobileApp && programs.find((p) => p.id === highlightProgramId);
    if (!target) return;
    const stageId = target.learningPillarId || UNASSIGNED_PILLAR.id;
    setOpenStages((prev) => (prev.has(stageId) ? prev : new Set(prev).add(stageId)));
  }, [isMobileApp, highlightProgramId, programs]);

  const gamificationBar = (
    <AdminGamificationBar
      completionByProgram={programs.reduce<Record<string, number>>((acc, p) => {
        acc[p.name] = p.programCompletionRate;
        return acc;
      }, {})}
      programsDue={adminProgramsDue(teamPrograms, Math.floor(Date.now() / 1000))}
    />
  );
  // Tabs while the release's answer is on its way too, so the phone doesn't start on the older layout.
  const phoneTabs = isMobileApp && learningGaps && (capabilityRelease || !capabilityReleaseResolved);
  const searchField = (
    <TextField
      value={searchTerm}
      onChange={(e) => setSearchTerm(e.target.value)}
      placeholder="Search"
      size="small"
      sx={{
        width: 300,
        flexShrink: 0,
        '& .MuiOutlinedInput-root': {
          bgcolor: 'rgba(0, 0, 0, 0.70)',
          borderRadius: '6px',
          color: '#FDFDFD',
          fontFamily: 'Inter, sans-serif',
          fontSize: 13,
          px: '12px',
          '& fieldset': { borderColor: '#444444' },
          '&:hover fieldset': { borderColor: '#565656' },
          '&.Mui-focused fieldset': { borderColor: '#F79420' }
        },
        '& .MuiOutlinedInput-input': { py: '8px', px: 0 },
        '& .MuiOutlinedInput-input::placeholder': { color: '#868686', opacity: 1 },
        // Full width on the phone, at 16px so iOS doesn't zoom in on focus. Last, to win over the above.
        [MOBILE_SX]: {
          width: '100%',
          '& .MuiOutlinedInput-root': { fontSize: 16 },
          '& .MuiOutlinedInput-input': { py: '10px' }
        }
      }}
      InputProps={{
        endAdornment: (
          <InputAdornment position="end">
            <SearchIcon sx={{ color: '#FDFDFD', fontSize: 18 }} />
          </InputAdornment>
        )
      }}
    />
  );

  return (
    <Box
      sx={{
        // Use a viewport-relative height instead of height: '100%' to avoid
        // the intermittent flex-height-inheritance bug where the columns
        // shrink to content height after navigating away and back.
        // 86px = ProgramHeader height; 50px = 2 × 25px padding from ProgramDetailsWrapper.
        height: 'calc(100vh - 86px - 50px)',
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        // Matches the platform chrome bg (#0F0F0F) so there's no visible
        // colour band across the top divider.
        bgcolor: '#0F0F0F',
        color: '#FDFDFD',
        px: 3,
        pt: 0.5,
        // The phone stacks the page and lets the app's content area scroll it.
        [MOBILE_SX]: { height: 'auto', px: 0, pt: 0 }
      }}
    >
      {/* Elliott actions orb — floats bottom-right (fixed), the same spot as the
          learner dashboard's orb. Consolidates Add New Program, Sync Knowledge,
          Invite Admin and Edit Framework. */}
      <FrameworkElliottMenu
        orgName={orgName}
        onOpen={() => {
          // Open the chat in "menu" mode — Elliott offers the framework actions.
          setAddDrawerPillarId(null);
          setAddDrawerEntry('menu');
          setAddDrawerOpen(true);
        }}
      />

      {/* Body — left column (gamification hub over the Gaps & Risks / Development
          Tier panel), Learning Framework on the right at full height. Gaps feed
          the framework: Elliott's recommended courses appear as cards at the top
          of the pillar their originating lesson sits in. */}
      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          pb: 3,
          display: 'flex',
          flexDirection: 'row',
          gap: 0,
          // Phone: one column. With the release, its tabs pick the section; before it, the panel, then the board.
          [MOBILE_SX]: { flex: '0 0 auto', flexDirection: 'column', gap: phoneTabs ? '8px' : '20px', pb: 0 }
        }}
      >
        {phoneTabs && (
          <PhoneSegments
            value={phoneView}
            options={[
              { key: 'programs', label: 'Programs' },
              { key: 'gaps', label: 'Capability' },
              { key: 'tier', label: 'Activity' }
            ]}
            onChange={(next) => {
              setPhoneView(next);
              if (next !== 'programs') changeInsightsView(next);
            }}
          />
        )}
        {learningGaps && !(phoneTabs && phoneView === 'programs') && (
          <Box
            sx={{
              flex: `0 0 ${GAPS_PANEL_WIDTH}`,
              minWidth: 0,
              minHeight: 0,
              display: 'flex',
              flexDirection: 'column',
              gap: '8px',
              mr: '8px',
              [MOBILE_SX]: { flex: '0 0 auto', mr: 0 }
            }}
          >
            {!capabilityReleaseResolved ? (
              <InsightsPanelSkeleton sx={{ flex: 1, minHeight: 0 }} />
            ) : (
              <InsightsPanel
                view={insightsView}
                onViewChange={changeInsightsView}
                tierMedals={tierMedals}
                tierLoading={teamTierLoading}
                audience={isOrgAdmin ? 'orgAdmin' : 'groupAdmin'}
                teamOptions={teamOptions}
                selectedTeamIds={selectedTeamIds}
                onTeamsChange={setSelectedTeamIds}
                teams={teamGaps}
                scoringTeams={allTeamGaps}
                loading={gapsData.loading}
                error={gapsData.error}
                teamsListMissing={!gapsData.loading && !gapsData.error && !gapsData.teamsList}
                lastComputedAt={gapsData.lastComputedAt}
                refreshing={gapsData.refreshing}
                onRefresh={gapsData.refresh}
                onSetStatus={gapsData.setStatus}
                onPublishCourse={handlePublishRecommendedCourse}
                onOpenKnowledgeHub={() => navigate('/app/knowledge')}
                onOpenOrgSettings={() => navigate('/app/orgsettings')}
                // Improve Lesson Content — open the originating program's designer,
                // where the lesson content, live scenario and quiz are edited.
                // Can't edit the lesson: message whoever can, in Dosen chat. They
                // are in a team manager's chat community (BE communityFilter).
                onMessageContact={(contact) =>
                  navigateToChat(contact.communicationId, true, personalThreadsArr, navigate, dispatch)
                }
                onImproveLesson={(gap) => {
                  if (!gap.programId) return;
                  navigate(`/app/programdetails/${orgId}/${coId}/${gap.programId}/designer`);
                }}
              />
            )}
          </Box>
        )}

        {/* Learning Framework — its own header (type filter + search), then the
            pillar columns. Columns stay at 25% of THIS panel, so four still fit
            across, just narrower than when the framework owned the full page. */}
        {/* Hidden, not removed, behind the phone's other tabs: the open stages and the search stay as they were. */}
        <Box
          sx={{
            flex: 1,
            minWidth: 0,
            display: phoneTabs && phoneView !== 'programs' ? 'none' : 'flex',
            flexDirection: 'column',
            [MOBILE_SX]: {
              flex: '0 0 auto',
              // The tabs name this section, and it sits in the same frame as Capability and Activity.
              ...(phoneTabs
                ? {
                    p: '12px',
                    bgcolor: '#1C1C1C',
                    border: '1px solid rgba(247, 148, 32, 0.45)',
                    borderRadius: '8px',
                    boxShadow: '0 0 28px rgba(247, 148, 32, 0.07)'
                  }
                : null)
            }
          }}
        >
          {isMobileApp ? (
            // Phone: the title (with Programs Due), search across the width, then the type chips in
            // one scrolling row. With the tabs, no title.
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: '10px', mb: '4px' }}>
              {phoneTabs ? (
                // With no chips to show the bar takes no room, so the search sits at the top of the frame.
                <Box sx={{ display: 'contents', '& > div:empty': { display: 'none' } }}>{gamificationBar}</Box>
              ) : (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px', minHeight: 32 }}>
                  <Typography sx={{ flex: 1, minWidth: 0, fontSize: 18, fontWeight: 700, color: '#FDFDFD' }}>
                    Learning Framework
                  </Typography>
                  {gamificationBar}
                </Box>
              )}
              {searchField}
              {/* The chip row runs to its frame's edges, so a chip cut at the edge reads as "more this way". */}
              <Box
                sx={{
                  mx: phoneTabs ? '-12px' : '-16px',
                  display: 'flex',
                  '& > *': { px: phoneTabs ? '12px' : '16px' }
                }}
              >
                <ProgramTypeFilter selected={selectedTypes} onToggle={toggleType} />
              </Box>
              {editMode && (
                <DosenButton tone="cta" sizeVariant="sm" onClick={handleEditLifecycle} sx={{ alignSelf: 'stretch' }}>
                  Save Changes
                </DosenButton>
              )}
            </Box>
          ) : (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: '10px',
                px: '14px',
                py: '10px',
                bgcolor: '#1C1C1C',
                borderBottom: '1px solid #2A2A2A',
                // The left panel is framed separately now, so the framework header
                // is rounded on both top corners either way.
                borderRadius: '8px 8px 0 0',
                minHeight: 56
              }}
            >
              {/* Section title — deliberately the same treatment as the Learning
                Gaps title so the two panels read as equal halves. Shown only
                when the Gaps panel is there to be told apart from: on its own,
                the framework IS the page, and the chrome header above already
                reads "LEARNING FRAMEWORK". */}
              {learningGaps && (
                <Typography sx={{ fontSize: 17, fontWeight: 700, color: '#FDFDFD', flexShrink: 0 }}>
                  {capabilityRelease || !capabilityReleaseResolved ? 'Training and Assessment' : 'Learning Framework'}
                </Typography>
              )}

              {/* Programs Due + Top 3 — just right of the title. Programs Due
                follows the Team selection when the left panel is on. */}
              {gamificationBar}

              {/* Program-type filter — Role Excellence / Development / Standards.
                Shows all types by default; a chip narrows the columns to it. */}
              {/* Spacers either side centre the type pills between the title and search. */}
              <Box sx={{ flex: 1 }} />
              <ProgramTypeFilter selected={selectedTypes} onToggle={toggleType} />
              <Box sx={{ flex: 1 }} />

              {/* Search — moved here from the page's top bar. */}
              {searchField}

              {/* Save Changes (edit mode) — moved here from the old top bar. */}
              {editMode && (
                <DosenButton tone="cta" sizeVariant="sm" onClick={handleEditLifecycle} sx={{ flexShrink: 0 }}>
                  Save Changes
                </DosenButton>
              )}
            </Box>
          )}

          {isMobileApp ? (
            // Phone: the stages one under another, each a tab that opens its cards.
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: '10px', mt: '8px' }}>
              <DragDropContext onDragEnd={handleDragEnd}>
                {frameworkFirstLoad && <FrameworkColumnsSkeleton cardHeight={CARD_HEIGHT} columns={1} cards={2} />}
                {boardColumns.map((column) => column.node)}
              </DragDropContext>
              {editMode && <AddNewColumnTile onClick={handleAddColumn} />}
            </Box>
          ) : (
            /* The same surface as the Capability / Activity panel beside it. */
            <Box sx={{ position: 'relative', flex: 1, minHeight: 0, bgcolor: '#1C1C1C', borderRadius: '0 0 8px 0' }}>
              <DragDropContext onDragEnd={handleDragEnd}>
                <Box
                  ref={columnsRef}
                  sx={{
                    // The real columns fade in where the placeholders were.
                    ...(!frameworkFirstLoad && fadeInSx),
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'row',
                    // No flex gap — each PillarColumn carries its own 20px padding (Figma).
                    gap: 0,
                    // Fewer than 4 columns sit centred. `safe` falls back to the start edge
                    // once they overflow, so a 5th column still scrolls from the left.
                    justifyContent: 'safe center',
                    overflowX: 'auto',
                    overflowY: 'hidden',
                    // No right-padding: nav arrows sit absolutely on top of the
                    // scroll container so the 25% column math stays clean.
                    // Custom thin dark scrollbar (matches the columns' vertical scroll)
                    // instead of the default OS horizontal bar.
                    scrollbarWidth: 'thin',
                    scrollbarColor: '#565656 transparent',
                    '&::-webkit-scrollbar': { height: '8px', width: '8px' },
                    '&::-webkit-scrollbar-track': { background: 'transparent' },
                    '&::-webkit-scrollbar-thumb': { background: '#565656', borderRadius: '4px' },
                    '&::-webkit-scrollbar-thumb:hover': { background: '#666666' }
                  }}
                >
                  {frameworkFirstLoad && <FrameworkColumnsSkeleton cardHeight={CARD_HEIGHT} />}

                  {boardColumns.map((column) => column.node)}

                  {editMode && <AddNewColumnTile onClick={handleAddColumn} />}
                </Box>
              </DragDropContext>

              {canScrollLeft && (
                <NavArrowButton direction="left" onClick={handleScrollLeft} aria-label="Scroll stage columns left" />
              )}
              {canScrollRight && (
                <NavArrowButton direction="right" onClick={handleScrollRight} aria-label="Scroll stage columns right" />
              )}
            </Box>
          )}
        </Box>
      </Box>

      <RecommendationReasonDialog
        recommendation={recommendationReason?.capability ?? null}
        onClose={() => setRecommendationReason(null)}
        onContinue={() => {
          if (!recommendationReason) return;
          const { course, pillarId, capability } = recommendationReason;
          setRecommendationReason(null);
          openRecommendedBuild(course, pillarId, capability);
        }}
      />
      <AssessmentReviewDialog
        assessment={openAssessment?.assessment ?? null}
        title={openAssessment?.card.name ?? ''}
        status={openAssessment?.info ?? null}
        onClose={() => setOpenAssessment(null)}
      />
      {resumeAssessment && (
        <ResumeAssessmentDialog draft={resumeAssessment} onClose={() => setResumeAssessment(null)} />
      )}
      <ProgramEditorModal
        program={editorProgram}
        pillars={pillars}
        orgMembers={orgMembers}
        onAudienceChange={(audienceRules) =>
          editorProgram && handleProgramFieldChange(editorProgram.id, { audienceRules })
        }
        onAudiencePreview={(rules) =>
          editorProgram ? previewProgramAudience(editorProgram.id, rules) : Promise.resolve(null)
        }
        onLaunchDateChange={(launchDate) => editorProgram && handleProgramFieldChange(editorProgram.id, { launchDate })}
        onOwnerChange={(owner) => editorProgram && handleProgramFieldChange(editorProgram.id, { owner })}
        onProgramTypeChange={(programType) => editorProgram && changeProgramType(editorProgram, programType)}
        onStageChange={(pillarId) => editorProgram && moveProgram(editorProgram.id, pillarId)}
        onRenamed={refetch}
        // A removal on People changes the card's completion: the board reads it again, fresh.
        onPeopleChanged={refetch}
        onClose={() => {
          setEditorProgramId(null);
          // A rename, lock or publish in the editor shows on the card.
          refetch();
        }}
      />
      {trainingBuild && (
        <BuildTrainingDialog
          recommended={trainingBuild.recommended}
          fresh={trainingBuild.fresh}
          resume={trainingBuild.resume}
          onProgramChanged={(grpId) => {
            trainingProgramId.current = grpId;
            refetch();
          }}
          onClose={() => {
            // Left part-way: wherever it was left, the program's card says so, and is scrolled to.
            const left = trainingBuild.resume?.groupId ?? trainingProgramId.current;
            trainingProgramId.current = null;
            setTrainingBuild(null);
            refetch();
            if (left) setHighlightProgramId(left);
          }}
          onGenerate={(recommendation) => {
            // A capability recommendation's Training is now under way: it is no longer a recommendation.
            // It is kept with the program, so this holds when the build starts on a later visit too.
            if (!recommendation) return;
            capabilityApi
              .setRecommendationStatus(recommendation.roleId, recommendation.capabilityId, 'published', capabilityOrg)
              .then(() => refreshCapability('admin'))
              .catch(() => undefined);
          }}
          onFinished={(grpId) => {
            // Built and its people invited: back on the dashboard, with its card scrolled to and highlighted.
            trainingProgramId.current = null;
            setTrainingBuild(null);
            localStorage.removeItem('aimd_v2_wizard_state');
            refetch();
            setHighlightProgramId(grpId);
          }}
        />
      )}
      <AddNewProgramDrawer
        open={addDrawerOpen}
        entry={addDrawerEntry}
        resumeGroupId={resumeDrawerGroup?.id ?? null}
        resumeProgramName={resumeDrawerGroup?.name}
        resumeMode={resumeDrawerGroup?.mode}
        orgName={orgName}
        isOrgAdmin={isOrgAdmin}
        pillars={pillars.map((p) => ({ id: p.id, name: p.name }))}
        onPillarSelect={(id) => setAddDrawerPillarId(id)}
        existingMembers={orgMembers}
        existingProgramNames={Array.from(new Set([...programs.map((p) => p.name), ...allOrgGroupNames]))}
        hasKnowledgeIntegration={hasKnowledgeIntegration}
        recommendedSeed={recommendedSeed}
        onBuildWithElliott={
          trainingFlow
            ? (format) => {
                // Open the flow first, then close this a beat later so the two backdrops
                // overlap: no flash of the un-dimmed dashboard between the two.
                setTrainingBuild({ fresh: { pillarId: addDrawerPillarId, guided: format === 'guided' } });
                window.setTimeout(() => {
                  setAddDrawerOpen(false);
                  setAddDrawerPillarId(null);
                }, 300);
              }
            : undefined
        }
        onSaveAudience={async (grpId, values) => {
          // Recommended Course review: the program already exists (created when
          // the category was picked), so persist the reviewed audience before
          // Structure. Save-only under the revamp — Invite & Finish enrols.
          const rules = chatAudienceRules(values.audience, values.audienceMatch);
          try {
            await API.patchProgram({
              id: grpId,
              body: {
                audienceRules: rules,
                enrollmentMode: rules.length > 0 ? 'proceed' : 'manual',
                ...(creationFlowV2 ? { enroll: false } : {})
              },
              ...getLLAuthHeaders()
            });
            // A capability recommendation's Training is now under way: it is no longer a recommendation.
            if (buildingRecommendation) {
              const { roleId, capabilityId } = buildingRecommendation;
              setBuildingRecommendation(null);
              capabilityApi
                .setRecommendationStatus(roleId, capabilityId, 'published', capabilityOrg)
                .then(() => refreshCapability('admin'))
                .catch(() => undefined);
            }
            return true;
          } catch (e: any) {
            toast.error(e?.response?.data?.message || "Couldn't save the audience. Please try again.");
            return false;
          }
        }}
        onEditFramework={() => {
          setAddDrawerOpen(false);
          handleEditLifecycle();
        }}
        onBuildFramework={
          frameworkEnabled
            ? () => {
                // Open the builder first, then close the Add drawer a beat later so the
                // two modal backdrops overlap - avoids a flash of the un-dimmed
                // dashboard between the two dialogs.
                setFrameworkBuilderOpen(true);
                window.setTimeout(() => setAddDrawerOpen(false), 300);
              }
            : undefined
        }
        frameworkBuilt={Boolean(frameworkStore.framework)}
        setupProgress={setupProgress}
        onOpenSetupGuide={() => {
          setAddDrawerOpen(false);
          setSetupGuideOpen(true);
        }}
        onSyncKnowledge={async () => {
          // Confirm a SharePoint knowledge integration exists for the org; if so,
          // kick off a sync. 404 / error → treat as not connected.
          try {
            const status = await API.getSharePointConnection(orgId);
            if (status?.isConnected) {
              API.triggerSharePointSync(orgId).catch(() => {});
              return true;
            }
            return false;
          } catch {
            return false;
          }
        }}
        onClose={() => {
          setAddDrawerOpen(false);
          setAddDrawerPillarId(null);
          setResumeDrawerGroup(null);
          setRecommendedSeed(null);
          setBuildingRecommendation(null);
        }}
        onSubmit={async (values, intent) => {
          if (!values.name.trim() || !values.programType) return;
          // POST /group/create with the LL fields packed into the same
          // request so the new program lands with pillar + type + audience
          // + owner + launch date already set. Refetch runs inside the
          // hook so the new card materialises on the dashboard.
          const ownerId = values.ownerMode === 'existing' ? values.existingOwnerId || null : null;
          const { id: createdId } = await createProgramFromDrawer({
            name: values.name,
            programType: values.programType,
            learningPillarId: addDrawerPillarId,
            audienceRules: chatAudienceRules(values.audience, values.audienceMatch),
            enrollmentMode: values.enrollmentMode,
            awaitingInviteFinish: creationFlowV2,
            launchDate: values.launchDate,
            ownerId
          });
          await inviteNewOwner(createdId, values);
          setAddDrawerOpen(false);
          setAddDrawerPillarId(null);
          // Brand-new program: wipe any in-progress draft from a previous
          // build (manual `programCreation` + AI `aimd_v2_wizard_state`) so the
          // new program never inherits another program's type/model/content.
          localStorage.removeItem('programCreation');
          localStorage.removeItem('aimd_v2_wizard_state');
          if (intent === 'design-now' && createdId) {
            navigate(`/app/programdetails/${orgId}/${coId}/${createdId}/new-program/start`);
          } else if (intent === 'design-now') {
            // Fallback to the legacy path if the create response didn't
            // surface a new id — the admin can still enter the flow and
            // pick the program manually.
            navigate(`/app/programdetails/${orgId}/${coId}/${groupId}/new-program/start`);
          }
        }}
        onCreateGroup={async (values) => {
          // Create the group up front (entering Build with Elliott) so the
          // Documents step can upload against it. No nav / no modal close —
          // the conversation continues. Mirrors the static create.
          if (!values.name.trim() || !values.programType) return null;
          const ownerId = values.ownerMode === 'existing' ? values.existingOwnerId || null : null;
          const { id } = await createProgramFromDrawer({
            name: values.name,
            programType: values.programType,
            learningPillarId: addDrawerPillarId,
            audienceRules: chatAudienceRules(values.audience, values.audienceMatch),
            enrollmentMode: values.enrollmentMode,
            awaitingInviteFinish: creationFlowV2,
            launchDate: values.launchDate,
            ownerId
          });
          await inviteNewOwner(id, values);
          return id;
        }}
        onElliottComplete={(grpId) => {
          // The embedded Structure → Content wizard finished (publish/Create).
          // Close the modal, stay on the framework, refetch so the new card
          // appears, then scroll to it + pulse a brandy highlight for ~5s.
          setAddDrawerOpen(false);
          setAddDrawerPillarId(null);
          setResumeDrawerGroup(null);
          localStorage.removeItem('programCreation');
          localStorage.removeItem('aimd_v2_wizard_state');
          refetch();
          if (grpId) setHighlightProgramId(grpId);
        }}
        onInviteAdmin={inviteAdmin}
        onPromoteOrgAdmin={promoteOrgAdmin}
        canInviteOrgAdmin={canInviteOrgAdmin}
        onCreateMentorship={async (payload) => {
          // Mentorship path: create the group (with the LL pillar/audience
          // packed in), flip it to the mentorship model, and persist the
          // objectives + match settings Elliott captured — mirroring the legacy
          // MentorshipProgramName + MentorshipObjectives steps — before the
          // modal embeds the Members page.
          if (!payload.programName.trim()) return null;
          const { id } = await createProgramFromDrawer({
            name: payload.programName,
            programType: payload.programType,
            learningPillarId: addDrawerPillarId,
            audienceRules: chatAudienceRules(payload.audience, payload.audienceMatch),
            enrollmentMode: payload.enrollmentMode,
            awaitingInviteFinish: creationFlowV2,
            launchDate: payload.launchDate,
            ownerId: payload.ownerMode === 'existing' ? payload.existingOwnerId || null : null
          });
          if (!id) return null;
          await inviteNewOwner(id, payload);
          try {
            const formData = new FormData();
            formData.append('model', 'mentorship');
            formData.append('includeTask', 'true');
            formData.append('allowEditing', 'true');
            formData.append('mentee', 'mentee');
            formData.append('mentor', 'mentor');
            formData.append('name', payload.programName.trim());
            await API.updateGroupInfo({ orgId: coId || orgId, grpId: id, formData });

            const objectives = payload.objectives.map((o) => o.trim()).filter(Boolean);
            await API.updateLearningObjectives({
              grpId: id,
              orgId,
              data: { number: objectives.length, objectives }
            });
            await API.updateAllMatches({
              grpId: id,
              orgId,
              data: {
                maxMatchesPerMentee: payload.maxMatchesPerMentee,
                maxMatchesPerMentor: payload.maxMatchesPerMentor,
                sessionsPerMatch: payload.sessionsPerMatch,
                matchRequest: false
              }
            });
            await API.updateTopicsAndIndustry({
              grpId: id,
              orgId,
              data: { topics: ['Topic Creation'] },
              key: 'topics'
            });
            await API.updateTopicsAndIndustry({
              grpId: id,
              orgId,
              data: { industries: ['Industry Creation'] },
              key: 'industries'
            });
            // Publish — mentorship has no publishTask equivalent; the legacy
            // final step PATCHed these flags. Without them the program stays
            // in draft and invites are limited to admins.
            const published = await publishMentorshipProgram(id);
            if (!published) {
              toast.error('The program was built but not published. Publish the program from the Dashboard.');
            }
          } catch (e) {
            console.error('[LL] mentorship setup failed', e);
            toast.error('Could not save the mentorship settings. Please try again.');
            return null;
          }
          return id;
        }}
      />

      {frameworkEnabled && (
        <LearningFrameworkBuilder
          open={frameworkBuilderOpen}
          onClose={() => setFrameworkBuilderOpen(false)}
          store={frameworkStore}
          orgName={orgName}
          userName={userName}
          pillars={pillars}
          onAddPrograms={async (progs) => {
            // Create each recommendation as a Design-status program tile, placed in
            // the pillar whose icon matches the recommendation's pillar key. Only
            // name + type + pillar are needed for the tile; audience / owner /
            // launch date are left for the admin to fill in on the card.
            // createProgramFromDrawer never throws: it toasts the error and returns
            // a null id. Resolve with the recommendation → program links actually
            // created so a failed one stays addable in the builder.
            const pillarIdByIcon = new Map(pillars.map((p) => [p.icon, p.id]));
            const created: { recommendationId: string; groupId: string; name: string }[] = [];
            for (const prog of progs) {
              const { id } = await createProgramFromDrawer({
                name: prog.name,
                programType: prog.type,
                learningPillarId: pillarIdByIcon.get(prog.pillar) ?? null,
                audienceRules: [],
                enrollmentMode: null,
                launchDate: null,
                ownerId: null
              });
              if (id) created.push({ recommendationId: prog.id, groupId: String(id), name: prog.name });
            }
            refetch();
            if (created.length) {
              toast.success(
                created.length === 1
                  ? `“${created[0].name}” added to the Dashboard`
                  : `${created.length} programs added to the Dashboard`
              );
            }
            return created;
          }}
        />
      )}

      <SetupGuideModal
        open={setupGuideOpen}
        onClose={() => setSetupGuideOpen(false)}
        variant={localStorage.getItem('role') === 'groupAdmin' ? 'groupAdmin' : 'orgAdmin'}
      />
    </Box>
  );
};

export default AdminLearningLifecycleDashboard;
